import { IProductCard } from '@src/components/product-card/product-card.types'
import { HubspotFormData } from '@src/quince-components/src/atoms/hubspot-form-modal'
import { TRADE_OPTIONS } from '@src/quince-components/src/molecules/tradeApplicationForm/constants'
import { ContentfulExperimentConfig } from '@src/quince-site-common/src/domains/ContentfulExperimentConfig'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'

import CUSTOM_SECTION_CONTAINER_NAMES from '../constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '../constants/customSectionContainerTypes'
import LAYOUT_TYPES from '../constants/layoutTypes'
import NAVIGATION_TYPE from '../constants/navigationType'
import POSITIONS from '../constants/positions'
import SUB_LAYOUT_TYPES from '../constants/subLayoutTypes'
import { THEME_TYPES } from '../constants/themeTypes'
import { TrackingBrowserEventTypes } from '../helpers/tracking-event/tracking-event'
import { ProductFeedMetaData } from '../services/productFeedService/ProductFeedService'
import { PRODUCT_FEED_TYPE } from '../services/productFeedService/productFeedService.types'

import { AirlineCompatibilityListing } from './AirlineCompatibilityListing'
import Button from './Button'
import CustomSectionType from './CustomSectionType'
import GenericContent from './GenericContent'
import Image from './Image'
import InfluencerDetails from './InfluencerDetails'
import Link from './Link'
import PartnerProduct from './PartnerProduct'
import RichText from './RichText'
import Video from './Video'
export interface FromContentfulOptions {
  isLiteProduct: boolean
  contentContainerPosition?: number
}

export interface TrackingContext {
  label?: string
  sectionId?: string
  sectionType?: string
  col?: number
  row?: number
  contentContainerType?: CUSTOM_SECTION_CONTAINER_TYPES
  contentContainerName?: CUSTOM_SECTION_CONTAINER_NAMES
  contentContainerDisplayName?: string
  id?: string
  trackingId?: string
  contentContainerPosition?: number
  index?: number
  productProperties?: ProductProperties
  horizontalPosition?: number
}

export interface TrackingMetaData {
  context?: TrackingContext
}

interface ImageAsset {
  id: string
  image: Image
  title: string
  type: 'IMAGE'
}

interface ImageAsset {
  id: string
  image: Image
  title: string
  type: 'IMAGE'
}
interface ICustomSection {
  readonly id: string
  readonly title?: string
  readonly displayTitle?: string
  slug?: string
  readonly seoTitle?: string
  readonly sectionType?: CustomSectionType | ''
  readonly productCards?: IProductCard[]
  readonly richTexts?: RichText[]
  readonly buttons?: Button[]
  readonly subSections?: CustomSection[]
  readonly images?: Image[]
  readonly genericContents?: GenericContent[]
  readonly classNames: string[]
  readonly influencers: InfluencerDetails[]
  readonly layoutType: LAYOUT_TYPES
  readonly mobileLayoutType?: LAYOUT_TYPES
  readonly position?: POSITIONS
  readonly subLayoutType?: SUB_LAYOUT_TYPES
  readonly baseDynamicClasses: string[]
  readonly mobileDynamicClassesOverrides: string[]
  readonly navigationType?: NAVIGATION_TYPE | null
  readonly classNamesMobileOverrides: string[]
  readonly productFeedType?: PRODUCT_FEED_TYPE | undefined
  readonly productFeedLimit?: number | undefined
  readonly productFeedMetaData?: ProductFeedMetaData | undefined
  readonly tracker?: string
  readonly bringInViewHash?: string
  readonly videos?: Video[]
  readonly themeType?: THEME_TYPES
  readonly partnerProducts?: PartnerProduct[]
  readonly partnerProduct?: PartnerProduct
  readonly containerLinks?: Link[]
  readonly imageLinks?: Link[]
  readonly sectionIndex?: number
  readonly trackingMetaData?: TrackingMetaData
  readonly experimentName?: string
  readonly runExperiment?: boolean
  readonly pageSubType?: string
  readonly trackingId?: string
  readonly trackingTypes?: TrackingBrowserEventTypes[]
  readonly numberOfProductsToShowInDesktop?: number
  readonly numberOfProductsToShowInMobile?: number
  readonly airlineCompatibilityListing?: AirlineCompatibilityListing[]
  readonly supportedTradeOptions?: TRADE_OPTIONS[]
  readonly experimentConfig?: ContentfulExperimentConfig[]
  readonly contentContainerType?: CUSTOM_SECTION_CONTAINER_TYPES
  readonly contentContainerName?: CUSTOM_SECTION_CONTAINER_NAMES
  readonly elementPosition?: number
  readonly fetchProductsFromAPI?: boolean
  readonly visual?: Image
  readonly media?: ImageAsset[]
  readonly hubspotFormData?: HubspotFormData
}

export default class CustomSection implements ICustomSection {
  // eslint-disable-next-line i18next/no-literal-string
  readonly typename = 'CustomSection' as const
  readonly id: string
  readonly baseDynamicClasses: string[]
  readonly mobileDynamicClassesOverrides: string[]
  readonly classNamesMobileOverrides: string[]
  readonly productFeedType: PRODUCT_FEED_TYPE | undefined
  readonly productFeedLimit: number | undefined
  readonly productFeedMetaData: ProductFeedMetaData | undefined
  slug?: string
  readonly seoTitle?: string
  readonly displayTitle?: string | undefined
  readonly position?: POSITIONS
  readonly subLayoutType?: SUB_LAYOUT_TYPES
  readonly buttons?: Button[]
  readonly images?: Image[]
  readonly productCards?: IProductCard[]
  readonly richTexts?: RichText[]
  readonly sectionType?: CustomSectionType | ''
  readonly subSections?: CustomSection[]
  readonly title?: string
  readonly genericContents?: GenericContent[]
  readonly classNames: string[]
  readonly influencers: InfluencerDetails[]
  readonly layoutType: LAYOUT_TYPES
  readonly mobileLayoutType?: LAYOUT_TYPES
  readonly navigationType?: NAVIGATION_TYPE | null
  readonly tracker?: string
  readonly bringInViewHash?: string
  readonly videos?: Video[]
  readonly themeType?: THEME_TYPES
  readonly partnerProducts?: PartnerProduct[]
  readonly partnerProduct?: PartnerProduct
  readonly containerLinks?: Link[]
  readonly imageLinks?: Link[]
  readonly sectionIndex?: number
  readonly trackingMetaData?: TrackingMetaData
  readonly experimentName?: string
  readonly runExperiment?: boolean
  readonly pageSubType?: string
  readonly trackingId?: string
  readonly trackingTypes?: TrackingBrowserEventTypes[]
  readonly numberOfProductsToShowInDesktop?: number
  readonly numberOfProductsToShowInMobile?: number
  readonly airlineCompatibilityListing?: AirlineCompatibilityListing[]
  readonly supportedTradeOptions?: TRADE_OPTIONS[]
  readonly experimentConfig?: ContentfulExperimentConfig[]
  readonly contentContainerType?: CUSTOM_SECTION_CONTAINER_TYPES
  readonly contentContainerName?: CUSTOM_SECTION_CONTAINER_NAMES
  readonly elementPosition?: number
  readonly fetchProductsFromAPI?: boolean
  readonly visual?: Image
  readonly media?: ImageAsset[]
  readonly hubspotFormData?: HubspotFormData

  constructor({
    id,
    displayTitle,
    buttons,
    images,
    richTexts,
    sectionType = '',
    subSections,
    title,
    genericContents,
    classNames,
    influencers,
    layoutType,
    mobileLayoutType,
    position,
    subLayoutType,
    baseDynamicClasses,
    mobileDynamicClassesOverrides,
    navigationType,
    classNamesMobileOverrides,
    productFeedType,
    productFeedLimit,
    productCards,
    productFeedMetaData,
    tracker,
    bringInViewHash,
    videos,
    themeType,
    partnerProducts,
    partnerProduct,
    containerLinks,
    imageLinks,
    sectionIndex,
    trackingMetaData,
    experimentName,
    runExperiment,
    pageSubType,
    trackingId,
    trackingTypes,
    numberOfProductsToShowInDesktop,
    numberOfProductsToShowInMobile,
    airlineCompatibilityListing,
    supportedTradeOptions,
    experimentConfig,
    contentContainerType,
    contentContainerName,
    elementPosition,
    fetchProductsFromAPI,
    visual,
    media,
    hubspotFormData,
  }: ICustomSection) {
    this.id = id
    this.displayTitle = displayTitle
    this.buttons = buttons
    this.images = images
    this.productCards = productCards
    this.richTexts = richTexts
    this.sectionType = sectionType
    this.subSections = subSections
    this.title = title
    this.genericContents = genericContents
    this.classNames = classNames
    this.influencers = influencers
    this.layoutType = layoutType
    this.mobileLayoutType = mobileLayoutType
    this.position = position
    this.subLayoutType = subLayoutType
    this.baseDynamicClasses = baseDynamicClasses
    this.mobileDynamicClassesOverrides = mobileDynamicClassesOverrides
    this.navigationType = navigationType
    this.classNamesMobileOverrides = classNamesMobileOverrides
    this.productFeedType = productFeedType
    this.productFeedLimit = productFeedLimit
    this.productFeedMetaData = productFeedMetaData
    this.tracker = tracker
    this.bringInViewHash = bringInViewHash
    this.videos = videos
    this.themeType = themeType
    this.partnerProducts = partnerProducts
    this.partnerProduct = partnerProduct
    this.containerLinks = containerLinks
    this.imageLinks = imageLinks
    this.trackingMetaData = trackingMetaData
    this.sectionIndex = sectionIndex
    this.experimentName = experimentName
    this.runExperiment = runExperiment
    this.pageSubType = pageSubType
    this.trackingId = trackingId
    this.trackingTypes = trackingTypes
    this.numberOfProductsToShowInDesktop = numberOfProductsToShowInDesktop
    this.numberOfProductsToShowInMobile = numberOfProductsToShowInMobile
    this.airlineCompatibilityListing = airlineCompatibilityListing
    this.supportedTradeOptions = supportedTradeOptions
    this.experimentConfig = experimentConfig
    this.contentContainerType = contentContainerType
    this.contentContainerName = contentContainerName
    this.elementPosition = elementPosition
    this.fetchProductsFromAPI = fetchProductsFromAPI
    this.visual = visual
    this.media = media
    this.hubspotFormData = hubspotFormData
    return {
      ...this,
    }
  }
}
